import { useMutation, useSuspenseQuery } from '@apollo/client';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  ConversationStarter,
  DEFAULT_DETAIL_LEVEL,
  DEFAULT_FORMAT,
  UploadedFile,
} from '@eluve/blocks';
import type { DetailLevel, Format } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import {
  getChatConversationStartersQuery,
  mergeStarters,
  useResolvedConversationStarters,
} from '@eluve/feature-chat-with-patient-data';
import { useTranslation } from '@eluve/i18n';
import { getPreferenceLabel } from '@eluve/llm-outputs';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import {
  chatSettingsFragment,
  deleteChatPreferenceMutation,
  deleteChatSettingsMutation,
  getChatPreferencesQuery,
  getChatSettingsQuery,
  insertChatPreferenceMutation,
  updateChatCustomInstructionsMutation,
  updateChatPreferenceMutation,
  updateContextSourcesMutation,
  updateConversationStartersMutation,
  updateFilesMutation,
  upsertChatSettingsMutation,
} from './chatSettingsOperations';

export interface PreferenceFormState {
  id: string;
  name: string;
  format: Format;
  detailLevel: DetailLevel;
  customInstructions: string;
  isNew?: boolean;
}

export { DEFAULT_DETAIL_LEVEL, DEFAULT_FORMAT };

/**
 * Shared data layer for the Chat settings tab and its drill-in pages.
 * Queries are suspense-based and cache-shared, so each page can mount the
 * hook independently.
 */
export const useChatSettings = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('chat_settings');
  const toast = useToast();
  const { t } = useTranslation();

  const { starters: resolvedDefaults, isLoading: isLoadingDefaults } =
    useResolvedConversationStarters(tenantId);

  useSuspenseQuery(getChatSettingsQuery, {
    variables: { tenantId, userId },
  });

  const { data: preferencesData, refetch: refetchPreferences } =
    useSuspenseQuery(getChatPreferencesQuery, {
      variables: { tenantId, userId },
    });

  const preferences = preferencesData?.tenantUserLlmOutputPreferences ?? [];

  const settings = useCompleteFragment({
    fragment: chatSettingsFragment,
    key: { tenantId, userId },
    strict: false,
  });

  const customInstructions = settings?.customInstructions ?? '';
  const contextSources = settings?.contextSources ?? [];
  const userCustomStarters = settings?.conversationStarters as
    | ConversationStarter[]
    | undefined;
  const conversationStarters = mergeStarters(
    userCustomStarters,
    resolvedDefaults as ConversationStarter[],
  );
  const files = (settings?.files ?? []) as UploadedFile[];

  const [upsertSettings] = useMutation(upsertChatSettingsMutation, {
    onCompleted: (data) => {
      if (data.insertTenantUserChatSettingsOne) {
        const created = data.insertTenantUserChatSettingsOne;
        analytics.capture('chat_settings_upserted', {
          tenantId,
          userId,
          customInstructions: created?.customInstructions,
          conversationStarters: created?.conversationStarters,
          contextSources: created?.contextSources,
          files: created?.files,
        });
      }
    },
  });

  const [updateCustomInstructions] = useMutation(
    updateChatCustomInstructionsMutation,
    {
      onCompleted: (data) => {
        if (data.updateTenantUserChatSettingsByPk) {
          const updated = data.updateTenantUserChatSettingsByPk;
          analytics.capture('chat_custom_instructions_update', {
            tenantId,
            userId,
            previous: customInstructions,
            updated: updated.customInstructions,
          });
        }
      },
      onError: () => toast.error(t('settings.chat.updateInstructionsFailed')),
    },
  );

  const [updateContextSources] = useMutation(updateContextSourcesMutation, {
    onCompleted: (data) => {
      if (data.updateTenantUserChatSettingsByPk) {
        const updated = data.updateTenantUserChatSettingsByPk;
        const previousSources = contextSources ?? [];
        const newSources = updated.contextSources ?? [];
        analytics.capture('chat_context_sources_update', {
          tenantId,
          userId,
          updated: newSources,
          added: newSources.filter((s) => !previousSources.includes(s)),
          removed: previousSources.filter((s) => !newSources.includes(s)),
        });
      }
    },
    onError: () => toast.error(t('settings.chat.updateContextSourcesFailed')),
  });

  const [updateConversationStarters] = useMutation(
    updateConversationStartersMutation,
    {
      onCompleted: (data) => {
        if (data.updateTenantUserChatSettingsByPk) {
          const previous = conversationStarters ?? [];
          const updated =
            (data.updateTenantUserChatSettingsByPk
              .conversationStarters as ConversationStarter[]) ?? [];
          const prevIds = new Set(previous.map((s) => s.id));
          const newIds = new Set(updated.map((s) => s.id));
          analytics.capture('chat_conversation_starters_update', {
            tenantId,
            userId,
            previous,
            updated,
            added: updated.filter((s) => !prevIds.has(s.id)),
            removed: previous.filter((s) => !newIds.has(s.id)),
            modified: updated.filter((s) => {
              const old = previous.find((p) => p.id === s.id);
              return (
                old &&
                (old.title !== s.title ||
                  old.description !== s.description ||
                  old.content !== s.content)
              );
            }),
          });
        }
      },
      onError: () => toast.error(t('settings.chat.updateStartersFailed')),
    },
  );

  const [updateFiles] = useMutation(updateFilesMutation, {
    onCompleted: (data) => {
      if (data.updateTenantUserChatSettingsByPk) {
        const previous = files ?? [];
        const updated =
          (data.updateTenantUserChatSettingsByPk?.files as UploadedFile[]) ??
          [];
        const prevIds = new Set(previous.map((f) => f.id));
        const newIds = new Set(updated.map((f) => f.id));

        analytics.capture('chat_files_update', {
          tenantId,
          userId,
          previous,
          updated,
          added: updated.filter((f) => !prevIds.has(f.id)),
          removed: previous.filter((f) => !newIds.has(f.id)),
        });
      }
    },
    onError: () => toast.error(t('settings.chat.updateFilesFailed')),
  });

  const [deleteSettings] = useMutation(deleteChatSettingsMutation, {
    update: () => {
      cacheUtils.evict({
        key: { tenantId, userId },
        typeName: 'TenantUserChatSettings',
      });
    },
    onCompleted: (data) => {
      if (data.deleteTenantUserChatSettingsByPk) {
        analytics.capture('chat_settings_reset', {
          tenantId,
          userId,
        });
      }
    },
    onError: () => toast.error(t('settings.chat.resetFailed')),
  });

  const [insertPreference] = useMutation(insertChatPreferenceMutation);
  const [updatePreference] = useMutation(updateChatPreferenceMutation);
  const [deletePreference] = useMutation(deleteChatPreferenceMutation);

  const availablePreferences = preferences.map((p) => ({
    id: p.id,
    label: getPreferenceLabel(p),
  }));

  const savePreference = async (preference: PreferenceFormState) => {
    if (preference.isNew) {
      await insertPreference({
        variables: {
          name: preference.name || null,
          format: preference.format,
          detailLevel: preference.detailLevel,
          customInstructions: preference.customInstructions || null,
        },
      });
      analytics.capture('chat_preference_created', {
        tenantId,
        userId,
        format: preference.format,
        detailLevel: preference.detailLevel,
      });
    } else {
      await updatePreference({
        variables: {
          tenantId,
          id: preference.id,
          name: preference.name || null,
          format: preference.format,
          detailLevel: preference.detailLevel,
          customInstructions: preference.customInstructions || null,
        },
      });
      analytics.capture('chat_preference_updated', {
        tenantId,
        userId,
        preferenceId: preference.id,
        format: preference.format,
        detailLevel: preference.detailLevel,
      });
    }
    await refetchPreferences();
  };

  const deletePreferenceWithUnlink = async (prefId: string) => {
    // Remove preferenceId from any conversation starters that reference it
    const updatedStarters = conversationStarters.map((s) =>
      s.preferenceId === prefId ? { ...s, preferenceId: undefined } : s,
    );
    const startersChanged = updatedStarters.some(
      (s, i) => s.preferenceId !== conversationStarters[i]?.preferenceId,
    );

    if (startersChanged && settings) {
      await updateConversationStarters({
        variables: {
          tenantId,
          userId,
          conversationStarters: updatedStarters,
        },
        refetchQueries: [getChatConversationStartersQuery],
      });
    }

    await deletePreference({
      variables: { tenantId, id: prefId },
    });

    cacheUtils.evict({
      typeName: 'TenantUserLlmOutputPreferences',
      key: { tenantId, id: prefId },
    });

    await refetchPreferences();

    analytics.capture('chat_preference_deleted', {
      tenantId,
      userId,
      preferenceId: prefId,
    });
  };

  const resetSettings = async () => {
    await deleteSettings({
      variables: { tenantId, userId },
    });
  };

  const saveInstructions = async (value: string) => {
    if (settings) {
      await updateCustomInstructions({
        variables: { tenantId, userId, customInstructions: value },
      });
    } else {
      await upsertSettings({
        variables: {
          customInstructions: value,
        },
      });
    }
  };

  const saveContextSources = async (sources: string[]) => {
    if (settings) {
      await updateContextSources({
        variables: { tenantId, userId, contextSources: sources },
      });
    } else {
      await upsertSettings({
        variables: {
          contextSources: sources,
        },
      });
    }
  };

  const saveConversationStarters = async (starters: ConversationStarter[]) => {
    if (settings) {
      await updateConversationStarters({
        variables: { tenantId, userId, conversationStarters: starters },
        refetchQueries: [getChatConversationStartersQuery],
      });
    } else {
      await upsertSettings({
        variables: {
          conversationStarters: starters,
        },
      });
    }
  };

  const saveFiles = async (uploadedFiles: UploadedFile[]) => {
    if (settings) {
      await updateFiles({
        variables: { tenantId, userId, files: uploadedFiles },
      });
    } else {
      await upsertSettings({
        variables: {
          files: uploadedFiles,
        },
      });
    }
  };

  return {
    tenantId,
    userId,
    customInstructions,
    contextSources,
    conversationStarters,
    isLoadingDefaults,
    files,
    preferences,
    availablePreferences,
    savePreference,
    deletePreferenceWithUnlink,
    resetSettings,
    saveInstructions,
    saveContextSources,
    saveConversationStarters,
    saveFiles,
  };
};
